import BaseModel from '@/data/models/base/base-model';
import TypeUtils from '@/helpers/type-utils';
import {IconMeter, MeterType, Status, UnitType} from './enums/meter-enum';
import {Provider} from './enums/provider-enum';
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

export class AlertingMeter {
  meterId: string;
  lastInsert: Date;
  meterName: string;
  buildingId: string;

  get fromNow(): string {
    return dayjs(this.lastInsert).fromNow();
  }

  static map(data: Partial<AlertingMeter>): AlertingMeter | null {
    if (!data) {
      return null;
    }

    const meter = new AlertingMeter();

    meter.meterId = TypeUtils.hasStringOrDefault(data.meterId);
    meter.meterName = TypeUtils.hasStringOrDefault(data.meterName);
    meter.buildingId = TypeUtils.hasStringOrDefault(data.buildingId);
    meter.lastInsert = TypeUtils.hasDateOrDefault(data.lastInsert);

    return meter;
  }
}

/** Dernière remontée d'un compteur, avec son bâtiment et son client (onglet « Flux » de l'administration). */
export class FlowMeter {
  meterId: string;
  meterName: string;
  meterType: MeterType;
  provider: Provider;
  buildingId: string;
  buildingName: string;
  clientName: string;
  lastInsert: Date | null;

  get fromNow(): string {
    return this.lastInsert ? dayjs(this.lastInsert).fromNow() : '';
  }

  get icon(): string {
    return IconMeter[this.meterType] || 'speed';
  }

  static map(data: Partial<FlowMeter>): FlowMeter | null {
    if (!data) {
      return null;
    }

    const meter = new FlowMeter();

    meter.meterId = TypeUtils.hasStringOrDefault(data.meterId);
    meter.meterName = TypeUtils.hasStringOrDefault(data.meterName);
    meter.meterType = data.meterType;
    meter.provider = TypeUtils.hasEnumOrDefault<Provider>(data.provider, Provider, Provider.NONE);
    meter.buildingId = TypeUtils.hasStringOrDefault(data.buildingId);
    meter.buildingName = TypeUtils.hasStringOrDefault(data.buildingName);
    meter.clientName = TypeUtils.hasStringOrDefault(data.clientName);
    meter.lastInsert = TypeUtils.hasDateOrDefault(data.lastInsert, null);

    return meter;
  }
}

export default class Meter extends BaseModel {

  buildingId: string;
  name: string;
  pricePerUnit: number;
  primary: boolean;
  referenceMeterId: string;
  isReference: boolean;
  type: MeterType;
  unit: UnitType;
  apiHash: string;
  status: Status;
  electricalMeterModelReferenceId: string = undefined;
  original: Meter = null;
  formula: string = "";
  drift_formula: string = "";
  // MAC du datalogger ESP32 qui mesure ce compteur (gestion à distance Wi-Fi / OTA), null sinon
  dataloggerMac: string = null;


  get isRelatedToReference(): boolean {
    return this.original !== null;
  }

  get isElectrical(): boolean {
    return this.type === MeterType.ELECTRICTY
  }

  get isArithmetic(): boolean {
    return this.formula != null && this.formula != "";
  }

  /**
   * Retourne la liste des meterIds référencés dans l'opération arithmétique
   */
  get arithmeticsReferencesIds(): string[] {
    if (!this.formula) return [];

    const regex = /{{(.*?)}}/g;
    const matches = this.formula.match(regex);
    return matches ? matches.map((it) => it.replace("{{", "").replace("}}", "").trim()) : [];
  }

  /**
   * Retourne la liste des meterIds référencés dans l'opération arithmétique ({{SUM(meter1)}}, {{AVG(meter2)}}), …)
   * Retourne : ["meter1", "meter2"]
   */
  get driftReferencesIds(): string[] {
    if (!this.drift_formula) return [];

    const regex = /{{(.*?)}}/g;
    const matches = this.drift_formula.match(regex);
    const idWithFunction = matches ? matches.map((it) => it.replace("{{", "").replace("}}", "").trim()) : [];

    return idWithFunction.map((it) => {
      if (!it.includes("(")) return it;
      return it.split("(")[1].split(")")[0];
    });
  }

  /**
   * Réalise le calcul de l'opération arithmétique
   * Cette méthode est appelé à chaque fois qu'une valeur d'un compteur référencé dans l'opération arithmétique est modifié.
   * @param payload : Les valeurs des compteurs référencés dans l'opération arithmétique
   */
  evaluateFormula(payload: Record<string, number>): number {
    if (!this.formula) {
      return 0;
    }

    let operation = this.formula;
    for (const key in payload) {

      // Search for {{key}} or {{ key }} and replace it by the value
      operation = operation.replace(new RegExp(`{{\\s*${key}\\s*}}`, "g"), payload[key].toString());
    }

    // Calcule de l'opération arithmétique
    return eval(operation);
  }

  static map(data: Partial<Meter>): Meter | null {
    if (!data) {
      return null;
    }

    const meter = new Meter();

    meter.id = TypeUtils.hasStringOrDefault(data.id);
    meter.buildingId = TypeUtils.hasStringOrDefault(data.buildingId);
    meter.name = TypeUtils.hasStringOrDefault(data.name);
    meter.pricePerUnit = TypeUtils.hasNumberOrDefault(data.pricePerUnit, 0);
    meter.type = TypeUtils.hasEnumOrDefault<MeterType>(data.type, MeterType, MeterType.HEAT);
    meter.unit = TypeUtils.hasEnumOrDefault<UnitType>(data.unit, UnitType, UnitType.Kwh);
    meter.apiHash = TypeUtils.hasStringOrDefault(data.apiHash, '');
    meter.primary = TypeUtils.hasBooleanOrDefault(data.primary, false);
    meter.referenceMeterId = TypeUtils.hasStringOrDefault(data.referenceMeterId);
    meter.isReference = TypeUtils.hasBooleanOrDefault(data.isReference, false);
    meter.status = TypeUtils.hasEnumOrDefault<Status>(data.status, Status, Status.active);
    meter.electricalMeterModelReferenceId = TypeUtils.hasStringOrDefault(data.electricalMeterModelReferenceId, undefined);

    meter.formula = TypeUtils.hasStringOrDefault(data.formula, "");
    meter.drift_formula = TypeUtils.hasStringOrDefault(data.drift_formula, "");
    meter.dataloggerMac = TypeUtils.hasStringOrDefault(data.dataloggerMac, null);

    return meter;
  }

  clone(): Meter {
    const clone = Meter.map(this);
    clone.id = "";
    clone.apiHash = "";
    clone.name = `${clone.name} (copie)`;
    return clone;
  }

  toJSON(create: boolean = false): Partial<Meter> {

    return create ? {
      'buildingId': this.buildingId,
      'name': this.name,
      'primary': this.primary,
      'pricePerUnit': this.pricePerUnit,
      'type': this.type,
      'unit': this.unit,
      'status': this.status,
      'referenceMeterId': this.referenceMeterId,
      'isReference': this.isReference,
      'electricalMeterModelReferenceId': this.electricalMeterModelReferenceId,
      'formula': this.formula ?? "",
      'drift_formula': this.drift_formula ?? "",
      'dataloggerMac': this.dataloggerMac || null,
    } : {
      'id': this.id,
      'buildingId': this.buildingId,
      'name': this.name,
      'primary': this.primary,
      'pricePerUnit': this.pricePerUnit,
      'type': this.type,
      'unit': this.unit,
      'apiHash': this.apiHash,
      'status': this.status,
      'referenceMeterId': this.referenceMeterId,
      'isReference': this.isReference,
      'electricalMeterModelReferenceId': this.electricalMeterModelReferenceId,
      'formula': this.formula ?? "",
      'drift_formula': this.drift_formula ?? "",
      'dataloggerMac': this.dataloggerMac || null,
    };
  }

  get statusColor(): string {
    switch (this.status) {
      case Status.active:
        return 'green';
      case Status.inactive:
        return 'orange';
      case Status.deleted:
        return 'red';
      default :
        return 'grey';
    }
  }

  get icon(): string {
    return IconMeter[this.type.toString()];
  }

}
